import { FileContext, FileKind } from '@/typings/api/files';
import { getIntlTranslation } from '@/utils/intl';
import { FolderOpenOutlined } from '@ant-design/icons';
import { Button, Input, Space } from 'antd';
import React, { useState } from 'react';
import { useIntl } from 'umi';
import FilePickerDrawer from './FilePickerDrawer';

import styles from './FileUrlInput.less';

interface FileUrlInputProps {
  value?: string;
  onChange: (url: string) => void;
  /** Called when editing the field is finished, for callers that batch saves. */
  onBlur?: () => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  placeholder?: string;
  kind?: FileKind;
  context?: FileContext;
}

/**
 * A URL field with the file library behind a button.
 *
 * Both halves are kept deliberately: pasting a URL is right for an asset that
 * already lives somewhere (a CDN, another system), and the library is right for
 * everything the partner owns — including the file they are about to upload.
 * Offering only the picker would strand the first case; only the field strands
 * the second, which is how an image ended up being uploaded to nowhere.
 */
const FileUrlInput: React.FC<FileUrlInputProps> = ({
  value,
  onChange,
  onBlur,
  disabled,
  size = 'middle',
  placeholder = 'https://…',
  kind = 'image',
  context,
}) => {
  const intl = useIntl();
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <>
      <Space.Compact className={styles.field}>
        <Input
          size={size}
          disabled={disabled}
          value={value ?? ''}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
        />
        <Button
          size={size}
          disabled={disabled}
          icon={<FolderOpenOutlined />}
          onClick={() => setPickerOpen(true)}
          title={getIntlTranslation(intl, {
            id: 'components.fileLibrary.browse',
            defaultMessage: 'Choose from the file library',
          })}
        />
      </Space.Compact>

      <FilePickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        kind={kind}
        context={context}
        onSelect={(file) => {
          onChange(file.url);
          onBlur?.();
        }}
      />
    </>
  );
};

export default FileUrlInput;
